<template>
	<vx-card card-border class="mb-base">
		<h2 class="mb-base">{{ resolveTitle }}</h2>

		<!-- Resource Configuration only for Calendar -->
		<vx-card :title="$t('Schedule.AssignResource')" class="mt-4 mb-4" v-if="baseUrl.includes('/apps/calendar')">
			<p>
				{{ $t("Schedule.AssignResourceText") }}
			</p>
			<div class="vx-row">
				<div class="vx-col w-full lg:w-1/3 mt-4">
					<div class="mb-2">
						<label class="vs-input--label">{{ $t("Resource.Resource") }}</label>

						<vx-input-group>
							<template slot="prepend">
								<div class="prepend-text bg-secondary">
									<span><feather-icon icon="UserIcon" svgClasses="h-4 w-4" /></span>
								</div>
							</template>

							<vs-input
								v-model="rcSelect"
								@keyup.enter="resourcePopupActive = true"
								:placeholder="$t('Resource.Resource')"
								@click="resourcePopupActive = true"
								:title="$t('Schedule.ClickToOpenAvailableResources')"
								:disabled="isEdit"
							/>

							<template slot="append">
								<div class="append-text bg-primary">
									<span
										><feather-icon
											icon="SearchIcon"
											svgClasses="h-4 w-4"
											@click.prevent="!isEdit ? (resourcePopupActive = true) : null"
											class="cursor-pointer"
											:title="$t('Schedule.ClickToOpenAvailableResources')"
										/>
									</span>
								</div>
							</template>
						</vx-input-group>
					</div>
				</div>
			</div>
		</vx-card>

		<!-- Assigned Events only for Edit -->
		<vx-card :title="$t('Schedule.VisitInformation')" collapse-action class="mb-4" v-if="isEdit">
			<p class="mb-3">
				{{
					unsavedEvents.length > 0
						? $t("Schedule.ReadyVisitsToAssignText")
						: $t("Schedule.NotReadyVisitsToAssign")
				}}
			</p>

			<vs-divider v-show="unsavedEvents.length > 0" />

			<AssignedEventsList
				v-if="unsavedEvents.length > 0"
				:events="unsavedEvents"
				:action="handleAssignedEventListActions"
			/>
		</vx-card>

		<!-- Visit Point Form Configuration -->
		<vx-card
			:title="isEdit ? $t('Schedule.UpdateVisitInformation') : $t('Schedule.VisitInformation')"
			class="mt-4 mb-4"
			v-if="toggleEventInformationForm"
		>
			<p>
				{{ isEdit ? $t("Schedule.UpdateVisitInformationText") : $t("Schedule.VisitInformationText") }}
			</p>

			<form>
				<div class="vx-row mt-base">
					<div class="vx-col w-full lg:w-1/3 mb-2">
						<small class="date-label">{{ $t("Activity.Activity") }}</small>
						<v-select
							id="select-activity"
							:closeOnSelect="true"
							:clearable="false"
							:class="{'vs-con-loading__container': enableClassLoading}"
							:disabled="isEdit || eventFinish"
							v-model="selectedActivityLocal"
							:options="activities"
							:dir="$vs.rtl ? 'rtl' : 'ltr'"
						>
							<template v-slot:no-options="{search, searching}">
								<template v-if="searching">
									{{ $t("general.NoResultsFoundFor") + " " }}<em>{{ search }}</em>
								</template>
							</template>
						</v-select>
					</div>

					<div class="vx-col w-full lg:w-1/3 mb-2" v-if="!isEdit">
						<small class="date-label">{{ $t("Schedule.Repeat") }}</small>
						<v-select
							class="w-full"
							:closeOnSelect="true"
							:multiple="enableMultiple"
							v-model="repeat"
							:options="repeat_options"
							:dir="$vs.rtl ? 'rtl' : 'ltr'"
							:disabled="eventFinish"
							@input="setSelected"
						>
							<template v-slot:no-options="{search, searching}">
								<template v-if="searching">
									{{ $t("general.NoResultsFoundFor") + " " }}<em>{{ search }}</em>
								</template>
							</template>
						</v-select>
					</div>

					<div class="vx-col w-full lg:w-1/3 mb-2">
						<small class="date-label">{{ $t("InitDate") }}</small>
						<datepicker
							:language="$vs.rtl ? langHe : langEn"
							name="start-date"
							:disabledDates="disabledDatesFrom"
							v-model="startDate"
							:disabled="eventFinish"
						></datepicker>
					</div>

					<div class="vx-col w-full lg:w-1/3 mb-2" v-show="toggleEndDate">
						<small class="date-label">{{ $t("EndDate") }}</small>
						<datepicker
							:language="$vs.rtl ? langHe : langEn"
							:disabledDates="disabledDatesTo"
							name="end-date"
							v-model="endDate"
							:disabled="eventFinish"
						></datepicker>
					</div>

					<div class="vx-col w-full lg:w-1/3 mb-2">
						<small class="date-label">{{ $t("InitHour") }}</small>

						<flat-pickr
							:config="configDateTimePicker"
							class="w-full"
							v-model="init_hour"
							name="init_hour"
							:placeholder="$t('Schedule.SelectInitTime')"
							:disabled="selectedActivityLocal.value <= 0 || eventFinish"
						/>

						<span class="text-danger text-sm" v-show="init_hour_error">{{
							$t("Schedule.ErrorProcess.InitHourError")
						}}</span>
					</div>

					<div class="vx-col w-full lg:w-1/3 mb-2">
						<small class="date-label">{{ $t("EndHour") }}</small>

						<flat-pickr
							:config="configDateTimePicker"
							v-model="end_hour"
							class="w-full"
							:placeholder="$t('Schedule.SelectEndTime')"
							v-validate="'truthy'"
							name="end_hour"
							:disabled="eventFinish"
						/>

						<span class="text-danger text-sm" v-show="errors.has('end_hour')">{{
							$t("Schedule.ValidEndOur")
						}}</span>
					</div>

					<div class="vx-col w-full lg:w-1/3 mb-2">
						<div>
							<label class="vs-input--label">{{ cardTitle("ASSIGN_VP") }}</label>

							<vx-input-group>
								<template slot="prepend">
									<div class="prepend-text bg-secondary">
										<span><feather-icon icon="MapPinIcon" svgClasses="h-4 w-4" /></span>
									</div>
								</template>

								<vs-input
									v-model="vpSelect"
									@keyup.enter="openVisitPointSearch"
									:placeholder="$t('VisitPoint')"
									@click="openVisitPointSearch"
									:disabled="!disableVisitPointSearch"
									v-validate="'required'"
									name="visit-point"
								/>

								<template slot="append">
									<div class="append-text bg-primary">
										<span
											><feather-icon
												icon="SearchIcon"
												svgClasses="h-4 w-4"
												@click.prevent="disableVisitPointSearch ? openVisitPointSearch() : null"
												class="cursor-pointer"
											/>
										</span>
									</div>
								</template>
							</vx-input-group>
							<span class="text-danger text-sm" v-show="errors.has('visit-point')"
								>{{ cardTitle("ASSIGN_VP") }} {{ $t("Required") }}</span
							>
						</div>
					</div>
				</div>

				<div class="vx-row">
					<div class="vx-col w-full mt-4 flex flex justify-start gap-4">
						<vs-checkbox v-model="checkBoxAddFields">{{ $t("Schedule.AdditionalFields") }}</vs-checkbox>
					</div>
				</div>

				<div class="vx-row">
					<div class="vx-col w-full mt-base mb-4" v-if="checkBoxAddFields">
						<p>{{ $t("Schedule.AdditionalFieldsAlert") }}</p>
					</div>

					<div class="vx-col w-full lg:w-1/3" v-if="checkBoxAddFields">
						<small class="date-label">{{ $t("Type") }}</small>
						<v-select
							id="select-category"
							:class="{'vs-con-loading__container': enableClassLoading}"
							:disabled="categories.length <= 0 || eventFinish"
							v-model="selectedCategory"
							:options="categoriesOptions"
							:dir="$vs.rtl ? 'rtl' : 'ltr'"
						>
							<template v-slot:no-options="{search, searching}">
								<template v-if="searching">
									{{ $t("general.NoResultsFoundFor") + " " }}<em>{{ search }}</em>
								</template>
							</template>
						</v-select>
					</div>

					<div
						class="vx-col w-full"
						v-if="dinamicFields.length > 0 && selectedCategory !== null && checkBoxAddFields"
					>
						<dinamic-fields
							ref="DinamicFields"
							:additionalFields="dinamicFields"
							:additionalInfo="additional_info"
							:category="selectedCategory.category"
							vx_col_class="vx-col md:w-1/3 w-full mt-2"
						></dinamic-fields>
					</div>
				</div>

				<div v-if="isEdit && selectedCategory === null" class="vx-row mt-2">
					<div v-for="(v, key) in additional_info" :key="key" class="vx-col w-full lg:w-1/3">
						<vs-input class="w-full mt-2" :label="key" :value="v" disabled />
					</div>
				</div>

				<div class="vx-row">
					<div class="vx-col w-full lg:w-1/3 mb-2">
						<vs-button
							v-if="!isEdit"
							class="w-full mt-6"
							@click="addMoreEvents"
							icon-pack="feather"
							icon="icon-plus-square"
							:disabled="!validForm"
							>{{ $t("Schedule.AddVisit") }}
						</vs-button>

						<vs-button
							v-if="isEdit"
							class="w-full mt-6"
							@click="rescheduleEvent"
							icon-pack="feather"
							icon="icon-calendar"
							:disabled="!validFormUpdate"
							>{{ $t("Schedule.RescheduleVisit") }}
						</vs-button>
					</div>
				</div>
			</form>
		</vx-card>

		<!-- No Saved Events -->
		<vx-card :title="$t('Schedule.ReadyVisitsToAssign')" collapse-action class="mb-4" v-if="!isEdit">
			<p class="mb-3">
				{{
					unsavedEvents.length > 0
						? $t("Schedule.ReadyVisitsToAssignText")
						: $t("Schedule.NotReadyVisitsToAssign")
				}}
			</p>

			<vs-divider v-show="unsavedEvents.length > 0" />

			<NoSavedEventList v-if="unsavedEvents.length > 0" :events="unsavedEvents" :action="removeUnsavedEvents" />
		</vx-card>

		<!-- Waypoint Configuration  -->
		<vx-card card-border class="mb-base" :title="$t('Waypoint.WaypointsConfiguration')">
			<p>{{ $t("Waypoint.WaypointText") }}</p>

			<div class="vx-row">
				<div class="vx-col w-full mt-4">
					<vs-checkbox v-model="checkBoxAddWaypoint" :disabled="unsavedEvents.length !== 1">{{
						$t("Waypoint.OpenWaypointConfiguration")
					}}</vs-checkbox>
				</div>
				<div class="vx-col w-full mt-2">
					<span class="text-danger text-sm ml-4" v-show="unsavedEvents.length > 1">{{
						$t("Waypoint.WaypointConfigurationNotAvailable")
					}}</span>

					<span class="text-danger text-sm ml-4" v-show="unsavedEvents.length === 0">{{
						$t("Waypoint.WaypointConfigurationNotAvailableText")
					}}</span>
				</div>
			</div>

			<vs-divider v-show="checkBoxAddWaypoint && unsavedEvents.length === 1" />

			<WaypointsConfigurations
				v-if="checkBoxAddWaypoint && unsavedEvents.length === 1"
				@handleChanges="waypointsConfigurationsHandleChanges"
				:data-waypoints="waypoints"
				:is-edit="isEdit"
				:visit-point="visitPointSelected"
			/>
		</vx-card>

		<!-- Follower Configuration
		<vx-card card-border class="mb-base" :title="$t('Follower.FollowersConfiguration')">
			<p>{{ $t("Follower.FollowersText") }}</p>

			<div class="vx-row">
				<div class="vx-col w-full mt-4">
					<vs-checkbox v-model="checkBoxAddWaypoint" :disabled="unsavedEvents.length !== 1">{{
						$t("Follower.FollowerConfigurationText")
					}}</vs-checkbox>
				</div>
				<div class="vx-col w-full mt-2">
					<span class="text-danger text-sm ml-4" v-show="unsavedEvents.length > 1">{{
						$t("Follower.FollowerConfigurationText")
					}}</span>

					<span class="text-danger text-sm ml-4" v-show="unsavedEvents.length === 0">{{
						$t("Follower.FollowerConfigurationText")
					}}</span>
				</div>
			</div>

			<vs-divider />
		</vx-card>
		 -->

		<div class="vx-row mt-4 mb-4">
			<div class="vx-col w-full lg:w-1/5">
				<vs-button
					class="w-full"
					icon="icon-x"
					icon-pack="feather"
					type="border"
					color="danger"
					@click="handleBtnCancel"
					>{{ $t("Cancel") }}
				</vs-button>
			</div>
			<div class="vx-col w-full lg:w-3/5"></div>
			<div class="vx-col w-full lg:w-1/5">
				<vs-button class="w-full" icon="icon-save" icon-pack="feather" @click="createEvents" v-show="!isEdit"
					>{{ $t("Save") }}
				</vs-button>
			</div>
		</div>

		<vs-popup :fullscreen="fullscreen" title="" :active.sync="popupActive" class="vs-popup--content-custom">
			<transition name="slide-fade">
				<vx-card :title="cardTitle('ASSIGN_VP')" card-border>
					<visit-point-list
						v-if="fullscreen"
						:customConfig="visitPointListConfig"
						:relatedIds="relatedIds"
						:resource_id="resourceSelected ? resourceSelected.id : null"
						ref="VisitPointList"
						:componentParent="moduleName"
						:drPointsActivity="drPointsActivity"
						@selected="handleVPSelect"
					>
						<vx-card ref="filterCard" :title="$t('Filters')" class="user-list-filters mb-8" no-shadow>
							<template slot="actions">
								<div class="flex flex-wrap justify-end">
									<template>
										<vs-button
											v-if="windowWidth > 620"
											:size="iconSize"
											type="flat"
											:title="$t('Search')"
											icon-pack="feather"
											icon="icon-search"
											color="dark"
											@click="searchVisitPointFilterCategory"
											>{{ $t("Search") }}
										</vs-button>
										<vs-button
											v-else
											:size="iconSize"
											type="flat"
											:title="$t('Search')"
											icon-pack="feather"
											icon="icon-search"
											color="dark"
											@click="searchVisitPointFilterCategory"
										>
										</vs-button>
									</template>
									<template>
										<vs-button
											v-if="windowWidth > 620"
											:size="iconSize"
											type="flat"
											:title="$t('Schedule.Refresh')"
											icon-pack="feather"
											icon="icon-refresh-cw"
											color="dark"
											@click="refres"
											>{{ $t("Schedule.Refresh") }}</vs-button
										>
										<vs-button
											v-else
											:size="iconSize"
											type="flat"
											:title="$t('Schedule.Refresh')"
											icon-pack="feather"
											icon="icon-refresh-cw"
											color="dark"
											@click="refres"
										></vs-button>
									</template>

									<vs-button
										:size="iconSize"
										:title="$t('Select')"
										icon-pack="feather"
										icon="icon-check"
										@click="popupActive = false"
										>{{ $t("Select") }}
									</vs-button>
								</div>
							</template>

							<div class="vx-row">
								<div class="vx-col w-full md:w-1/2">
									<label class="vs-input--label">{{ $t("SearchBy") }}</label>
									<v-select
										v-model="searchField"
										:clearable="false"
										:options="searchOptions"
										name="country"
										:dir="$vs.rtl ? 'rtl' : 'ltr'"
										class="md:mb-1 mb-4"
									>
										<template v-slot:no-options="{search, searching}">
											<template v-if="searching">
												{{ $t("general.NoResultsFoundFor") + " " }}<em>{{ search }}</em>
											</template>
										</template>
									</v-select>
								</div>

								<div class="vx-col w-full md:w-1/2" v-if="searchField.value === 'notSchedule'">
									<div class="vx-row">
										<div class="vx-col w-1/2" v-show="searchField.value === 'notSchedule'">
											<label class="text-sm opacity-75">{{ $t("InitDate") }}</label>
											<datepicker
												:language="$vs.rtl ? langHe : langEn"
												class="w-full"
												name="start-date"
												v-model="startDateFilter"
											></datepicker>
										</div>
										<div class="vx-col w-1/2" v-show="searchField.value === 'notSchedule'">
											<label class="text-sm opacity-75">{{ $t("EndDate") }}</label>
											<datepicker
												:language="$vs.rtl ? langHe : langEn"
												class="w-full"
												:disabledDates="disabledDatesTo"
												name="end-date"
												v-model="endDateFilter"
											></datepicker>
										</div>
									</div>
								</div>

								<div class="vx-col w-full md:w-1/2" v-else>
									<div class="vx-row">
										<div
											class="vx-col w-full md:w-1/1"
											v-if="searchField.value === 'AdditionalFields'"
										>
											<div class="vx-row">
												<div class="vx-col w-full md:w-2/3">
													<label class="vs-input--label">{{
														$t("VisitPointObject.Create.SelectObjectType")
													}}</label>
													<v-select
														v-model="additionalField"
														:clearable="false"
														:options="categoriesVisitPoint"
														name="country"
														:dir="$vs.rtl ? 'rtl' : 'ltr'"
														value="typology_name"
														label="typology_name"
														class="w-full"
													>
														<template v-slot:no-options="{search, searching}">
															<template v-if="searching">
																{{ $t("general.NoResultsFoundFor") + " "
																}}<em>{{ search }}</em>
															</template>
														</template>
													</v-select>
												</div>
												<div class="vx-col w-full md:w-1/3 md:mt-1 mt-4">
													<label class="vs-input--label">{{
														$t("Preferences.FindExactly")
													}}</label>
													<vs-switch class="mt-2" color="success" v-model="exact_values" />
												</div>
											</div>
										</div>
										<div class="vx-col w-full md:w-1/1" v-else>
											<vs-input
												:placeholder="$t('Search')"
												v-model="searchQuery"
												@keyup.enter="searchVisitPointFilterCategory"
												class="w-full mt-2 md:mt-6"
											/>
										</div>
									</div>
								</div>
							</div>

							<dinamic-fields
								v-if="additionalField"
								ref="DinamicFields"
								:additionalFields="model"
								:additionalInfo="additional_info"
								:category="additionalField"
								class="mt-4"
							></dinamic-fields>
						</vx-card>
					</visit-point-list>
				</vx-card>
			</transition>
		</vs-popup>

		<vs-popup
			:fullscreen="true"
			:title="$t('ResourceList')"
			:active.sync="resourcePopupActive"
			class="vs-popup--content-custom"
		>
			<resource-list
				ref="ResourceList"
				:removeColumns="true"
				:customConfig="resourceListConfig"
				:componentParent="'ResourceListVisitPoint'"
				@showPopupEmmit="showPopup"
				@selected="handleResourceSelected"
			/>
		</vs-popup>
	</vx-card>
</template>

<script>
import {Validator} from "vee-validate";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import Datepicker from "vuejs-datepicker";
import {en, es} from "vuejs-datepicker/src/locale";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import vSelect from "vue-select";
require("vue-simple-calendar/static/css/default.css");
import DinamicFields from "@/components/DinamicFields/DinamicFields.vue";
import VisitPointList from "../visit-point/list/VisitPointList.vue";
import ResourceList from "@/components/resource/user-list/ResourceList.vue";
// Store
import moduleActivity from "@/store/activity/moduleActivity.js";
import moduleSchedule from "@/store/schedule/moduleSchedule.js";
import moduleVisitPoint from "@/store/visit-point/moduleVisitPoint.js";
import moduleResource from "@/store/resource/moduleResource.js";
import moduleCommon from "@/store/common/moduleCommon.js";

import Event from "@/models/Event.js";

import DateUtils from "@/helpers/DateUtils.js";
import Commons from "@/helpers/commons.js";
import CalendarSidebar from "./CalendarSidebar";
import CalendarAddEvent from "./CalendarAddEvent";

import {dict} from "./messageValidator.js";
import {BatteryChargingIcon, BatteryIcon, WifiIcon, CalendarIcon, MapPinIcon} from "vue-feather-icons";
import WaypointList from "./WaypointList.vue";
import Waypoint from "../../models/Waypoint.js";
import WaypointDialogForm from "./WaypointDialogForm.vue";
import NoSavedEventList from "./NoSavedEventList.vue";
import WaypointsConfigurations from "./WaypointsConfigurations.vue";
import AssignedEventsList from "./AssignedEventsList.vue";

// register custom messages
Validator.localize("en", dict);

export default {
	components: {
		Datepicker,
		flatPickr,
		vSelect,
		VisitPointList,
		CalendarSidebar,
		CalendarAddEvent,
		VuePerfectScrollbar,
		BatteryChargingIcon,
		BatteryIcon,
		WifiIcon,
		CalendarIcon,
		DinamicFields,
		MapPinIcon,
		ResourceList,
		WaypointList,
		WaypointDialogForm,
		NoSavedEventList,
		WaypointsConfigurations,
		AssignedEventsList,
	},
	data() {
		return {
			event: new Event({}),

			disabledFrom: false,
			isEdit: false,
			id: 0,
			address: "",
			city: "",
			startDate: new Date(),
			endDate: new Date(),

			langHe: es,
			langEn: en,

			init_hour: null,
			init_hour_error: false,
			end_hour: null,
			configDateTimePicker: {
				enableTime: true,
				enableSeconds: true,
				noCalendar: true,
			},
			selectedActivity: {value: 0, label: this.$t("Select")},
			active: false,

			// Repeat select
			repeat: [{label: this.$t("Schedule.None"), value: "NONE"}],
			repeatFormatString: "NONE",
			repeatOptions: [
				{label: this.$t("Schedule.None"), value: "NONE"},
				{label: this.$t("Schedule.WeekMayus"), value: "WEEK"},
				{label: this.$t("Schedule.MonthMayus"), value: "MONTH"},
				{label: this.$t("Schedule.EveryDay"), value: "MO|TU|WE|TH|FR|SA|SU"},
				{label: this.$t("Schedule.MondayToFriday"), value: "MO|TU|WE|TH|FR"},
				{label: this.$t("Schedule.Monday"), value: "MO"},
				{label: this.$t("Schedule.Tuesday"), value: "TU"},
				{label: this.$t("Schedule.Wednesday"), value: "WE"},
				{label: this.$t("Schedule.Thursday"), value: "TH"},
				{label: this.$t("Schedule.Friday"), value: "FR"},
				{label: this.$t("Schedule.Saturday"), value: "SA"},
				{label: this.$t("Schedule.Sunday"), value: "SU"},
			],

			//Config Visit Point List
			visitPointListConfig: {
				rowSelection: "single", // multiple | single
				activeCheckbox: true,
				activeHeaderCheckboxSelection: false,
				activeLink: false,
				columns: [
					"id",
					"name",
					"identification",
					"address",
					"city",
					"typology_name",
					"status_name",
					"additional_info",
				],
				tableOptions: false,
				showItemsPerPage: false,
				showSearch: false,
				showActions: false,
			},
			resourceListConfig: {
				rowSelection: "single",
				activeCheckbox: true,
				columns: ["id", "name", "lastname", "username", "email", "identification", "alias", "status"],
				actionDropdown: false,
				btnAssigResources: true,
				popupMode: true,
				assignedResourcesList: [],
				activeHeaderCheckboxSelection: true,
			},
			// Id list for default checks
			relatedIds: [],
			visitPointRelated: null,
			fullscreen: false,

			enableClassLoading: false,
			categories: [],
			selectedCategory: null,
			additional_info: {},
			additional_info_new: {},
			dinamicFields: [],
			activeAlert: true,
			// perfectscrollbar settings
			settings: {
				maxScrollbarLength: 60,
				wheelSpeed: 0.6,
			},
			enableMultiple: true,
			drPointsActivity: 0,
			infoView: true,
			visitPointView: false,
			showUnsavedEvents: false,

			// For additional filters
			searchQuery: "",

			searchField: {value: "Name", label: this.$t("Name")},
			dataSelect: [],
			additionalField: null,
			model: null,

			status_id: [],
			exact_values: false,

			startDateFilter: new Date(),
			endDateFilter: new Date(),
			popupActive: false,
			vpSelect: "",
			rcSelect: "",
			resourcePopupActive: false,
			checkBoxAddFields: false,
			checkBoxAddWaypoint: false,
			waypoints: [],
			dataEvent: null,
			toggleEventInformationForm: true,
			visitPointSelected: null,
			resourceSelected: null,
			activityList: [],
		};
	},
	computed: {
		baseUrl() {
			const path = this.$route.path;
			return path;
		},
		moduleName() {
			const path = this.$route.path;
			return path.includes("on-demand") ? "ON_DEMAND" : "calendar";
		},
		toggleEndDate() {
			return !this.isEdit && !this.repeat.some((r) => r.value === "NONE");
		},

		eventFinish() {
			return this.event.event_status_id === 7;
		},
		eventStarted() {
			return this.event.event_status_id === 6;
		},
		calendarDeletePermission() {
			return this.$store.getters.checkPermission("CALENDAR_D");
		},
		calendarUpdatePermission() {
			return this.$store.getters.checkPermission("CALENDAR_U");
		},

		categoriesOptions() {
			return this.categories.map((c) => ({
				label: c.typology_name,
				value: c.category_id,
				category: c,
			}));
		},

		currentVisitPointId() {
			return this.visitPointSelected ? this.visitPointSelected.id : 0;
		},

		activities() {
			if (this.activityList.length === 0) return [];

			if (this.moduleName === "ON_DEMAND") return this.generateOptionSelect(this.activityList, "name", "id");

			// Filter the activities that have the resource selected assigned

			if (this.moduleName === "calendar" && this.resourceSelected) {
				const resourceActivities = this.resourceSelected.activity_id.map((act) => {
					const activity = this.activityList.find((a) => a.id === act);
					if (activity) return activity;
				});
				return this.generateOptionSelect(resourceActivities, "name", "id");
			}
		},
		selectedActivityLocal: {
			get() {
				return {
					value: this.selectedActivity.value,
					label: this.selectedActivity.label,
				};
			},
			set(val) {
				this.selectedActivity = {...val};
			},
		},

		validForm() {
			return (
				this.selectedActivityLocal.value > 0 &&
				this.init_hour !== null &&
				this.end_hour !== null &&
				this.startDate !== "" &&
				this.endDate !== "" &&
				!this.errors.has("end_hour")
			);
		},
		validFormUpdate() {
			return (
				this.selectedActivityLocal.value > 0 &&
				this.init_hour !== null &&
				this.end_hour !== null &&
				this.startDate !== "" &&
				Date.parse(this.startDate) - new Date() >= 0
			);
		},

		labelColor() {
			return (label) => {
				if (label === 8) return this.$t("Schedule.Finished");
				else if (label === 9) return this.$t("Schedule.InProgress");
				else if (label === 5) return this.$t("Schedule.ReProgramming");
				else if (label === 4) return this.$t("Schedule.Canceled");
				else if (label === 2) return this.$t("Schedule.Scheduled");
			};
		},
		windowWidth() {
			return this.$store.state.windowWidth;
		},
		unsavedEvents() {
			if (!moduleSchedule.isRegistered) {
				this.$store.registerModule("moduleSchedule", moduleSchedule);
				moduleSchedule.isRegistered = true;
				return this.$store.state.moduleSchedule.unsavedEvents;
			}
			return this.$store.state.moduleSchedule.unsavedEvents;
		},
		repeat_options() {
			return this.repeatOptions;
		},
		enableVisitPointSelection() {
			return this.selectedActivityLocal.value <= 0;
		},
		categoriesVisitPoint() {
			return this.$store.state.moduleCommon.dataSelectVisitPoint.category;
		},

		iconSize() {
			return this.$store.state.windowWidth < 620 ? "small" : "medium";
		},

		searchOptions() {
			return [
				{value: "Name", label: this.$t("Name")},
				{value: "Identification", label: this.$t("Identification")},
				{value: "Address", label: this.$t("Address")},
				{value: "City", label: this.$t("City")},
				{
					value: "AdditionalFields",
					label: this.$t("Object.AdditionalFields"),
				},
				{
					value: "notSchedule",
					label: this.$t("VisitPointObject.List.NotSchedule"),
				},
			];
		},
		disabledDatesTo() {
			return {to: new Date(this.startDate)};
		},
		disabledDatesFrom() {
			return {to: new Date()};
		},
		buttonSize() {
			return this.windowWidth < 620 ? "small" : "medium";
		},
		buttonTitle() {
			return function (params) {
				const obj = {
					SEARCH_VP: `${this.$t("Search")} ${this.$t("VisitPoint")}`,
				};
				return obj[params] ? obj[params] : "";
			};
		},
		cardTitle() {
			return function (params) {
				const obj = {
					ASSIGN_VP: `${this.$t("VisitPointObject.Assig")} ${this.$t("VisitPoint")}`,
				};
				return obj[params] ? obj[params] : "";
			};
		},
		disableVisitPointSearch() {
			return this.isEdit || this.eventFinish || this.selectedActivityLocal.value;
		},

		resolveTitle() {
			const calendarMessage = this.isEdit
				? this.$t("Schedule.VisitRescheduling", {n: ""})
				: this.$t("Schedule.VisitScheduling", {n: ""});
			const onDemandMessage = this.isEdit
				? this.$t("Schedule.VisitRescheduling", {n: "On Demand"})
				: this.$t("Schedule.VisitScheduling", {n: "On Demand"});

			return this.moduleName === "ON_DEMAND" ? onDemandMessage : calendarMessage;
		},
	},
	methods: {
		setSelected(selections) {
			if (Array.isArray(selections)) {
				const lastSelected = selections[selections.length - 1];
				if (!lastSelected) {
					this.repeat = [{label: this.$t("Schedule.None"), value: "NONE"}];
					return;
				}
				if (
					lastSelected.value === "MO" ||
					lastSelected.value === "TU" ||
					lastSelected.value === "WE" ||
					lastSelected.value === "TH" ||
					lastSelected.value === "FR" ||
					lastSelected.value === "SA" ||
					lastSelected.value === "SU"
				) {
					const filter = selections.filter(
						({value}) =>
							value === "MO" ||
							value === "TU" ||
							value === "WE" ||
							value === "TH" ||
							value === "FR" ||
							value === "SA" ||
							value === "SU"
					);
					this.repeat = filter;
				} else {
					//this.enableMultiple = false;
					this.repeat = [lastSelected];
				}
			}
		},
		openVisitPointSearch() {
			this.fullscreen = true;
			this.popupActive = true;
		},

		openLoading() {
			this.$vs.loading();
		},
		closeLoading() {
			this.$vs.loading.close();
		},

		generateOptionSelect(data, propLabel, propValue) {
			let newData = [];
			if (this.moduleName === "ON_DEMAND") {
				newData = data
					.filter((d) => d.dr_points !== null)
					.map((d) => {
						return {
							label: d[propLabel] + " - DR Points: $ " + (d.dr_points || this.$t("OnDemand.Free")),
							value: d[propValue],
						};
					});
			} else {
				newData = data.map((d) => {
					return {label: d[propLabel], value: d[propValue]};
				});
			}
			newData.unshift({label: this.$t("Select"), value: 0});
			return newData;
		},

		validateForm() {
			return !this.errors.any();
		},

		validateEventHour() {
			if (this.stringTimeToDate(this.end_hour) < this.stringTimeToDate(this.init_hour)) {
				this.$vs.notify({
					color: "danger",
					title: this.$t("Schedule.InvalidFields"),
					text: this.$t("Schedule.ValidEndOur"),
				});
				return false;
			}
			return true;
		},

		/**
		 * Send the unsaved events data to the store to create events and waypoints if necessary
		 *
		 * @async
		 * @function createEvents
		 * @returns {void}
		 */

		async createEvents() {
			const events = this.$store.state.moduleSchedule.unsavedEvents.map((event) => new Event(event));

			const payload = {
				events: events.map((event) => event.getDataToSave()),
			};

			if (this.moduleName === "ON_DEMAND") {
				payload.events = events.map((event) => event.getDataToSaveOnDemand());
			}

			this.openLoading();

			const response = await this.$store.dispatch("moduleSchedule/createEvents", payload);

			this.closeLoading();

			if (response.hasOwnProperty("error") && response.error.length > 0) {
				response.error.forEach((error) => {
					this.validateErrorMessages(error.message);
				});

				return;
			}

			// recovery the waypoints from the store and add to the event
			// const waypoints = this.$store.state.moduleSchedule.waypoints;

			if (this.waypoints.length > 0) {
				const eventId = response.data[0].id;
				if (eventId) {
					this.createScheduleWaypoint(eventId);
					return;
				}
			}

			this.$vs.notify({
				color: "success",
				title: this.$t("TitleConfirmEventCreate"),
				text: this.$t("TextConfirmEventCreate"),
			});

			this.handleRedirect();
		},

		/**
		 * Reschedules an event by validating the event hour, generating a new event object,
		 * and dispatching an action to update the event in the store. Displays loading indicators
		 * and notifications based on the response status.
		 *
		 * @async
		 * @function rescheduleEvent
		 * @returns {void}
		 */
		async rescheduleEvent() {
			if (!this.validateEventHour()) return;

			const newEvent = this.generateNewEventToUpdate();

			this.openLoading();

			const response = await this.$store.dispatch("moduleSchedule/rescheduleEvent", newEvent.getDataToUpdate());

			this.closeLoading();

			if (response.status === 201) {
				if (response.error && response.error.length > 0) {
					this.validateErrorMessages(response.error[0].message);
					return;
				}

				this.$vs.notify({
					color: "success",
					title: this.$t("TitleConfirmEventUpdate"),
					text: this.$t("TextConfirmEventUpdate"),
				});

				this.handleRedirect();
			} else {
				this.showErrorAlert();
			}
		},

		/**
		 * Remove an event from the resource selected schedule
		 *
		 * @returns {Promise<void>}
		 */
		async removeEvent() {
			const payload = {
				events: [{event_id: this.dataEvent.id, resource_id: this.resourceSelected.id}],
			};

			this.openLoading();

			try {
				const response = await this.$store.dispatch("moduleSchedule/removeEvent", payload);

				if (response.status === 201) {
					if (response.hasOwnProperty("error") && response.error.length > 0) {
						this.validateErrorMessages(response.error[0].message);
						this.showErrorAlert();
						return;
					}

					this.$vs.notify({
						color: "success",
						title: this.$t("TitleConfirmEventDelete"),
						text: this.$t("TextConfirmEventDelete"),
					});

					this.handleRedirect();
				}
			} catch (error) {
				this.showErrorAlert();
			} finally {
				this.closeLoading();
			}
		},

		validateErrorMessages(message, params = "") {
			let text = "";
			switch (message) {
				case "activity not available for the resource":
					text = this.$t("Schedule.ActivityNoAvailable");
					break;
				case "init_hour less than current time":
					text = this.$t("Schedule.ErrorProcess.InitHourError");
					this.init_hour_error = true;
					break;
				case "visit point is not related to resource":
					text = this.$t("Schedule.ErrorProcess.VisitpointError");
					break;
				case "resource has no activities":
					text = this.$t("Schedule.ErrorProcess.ResourceActivityError");
					break;
				case "the visit point does not have enough dr points for this activity":
					text = this.$t("Schedule.ErrorProcess.VisitPointNotHaveEnoughDRPoints", {n: params});
					break;
				default:
					text = this.$t("ErrorMessage");
					break;
			}
			this.$vs.notify({
				time: 4000,
				color: "danger",
				title: this.$t("ErrorTitle"),
				text,
			});
		},

		manageErrorAlert(err) {
			const {title, text} = Commons.manageErrorValidator.call(this, err);
			const findErrorNameProperty = Commons.findErrorNameProperty();
			const nameProperty = findErrorNameProperty(err);

			/* this.alert_title = this.$t("Routing.Messages.MatchColumnSelectionRoutingErrorTitle");
      this.alert_message = `${this.$t("Routing.Messages.MatchColumnSelectionRoutingError")} ${nameProperty.toUpperCase()}`; */
		},

		showErrorAlert() {
			this.$vs.notify({
				color: "danger",
				title: this.$t("Schedule.ErrorProcess.Title"),
				text: this.$t("Schedule.ErrorProcess.Text"),
			});
		},
		async cleanEvents() {
			await this.$store.dispatch("moduleSchedule/cleanEvents");
		},
		formatRepeatValue(days) {
			if (Array.isArray(days)) {
				const letters = days.map((d) => {
					if (d === "TUESDAY" || d === "THURSDAY") {
						return d.substring(0, 2);
					} else {
						return d.substring(0, 1);
					}
				});
				return letters.join("|");
			} else {
				return days;
			}
		},

		stringTimeToDate(stringTime) {
			let newDate = new Date();
			if (typeof stringTime === "string") {
				const [hours, minutes, seconds] = stringTime.split(":");
				newDate = new Date(1995, 11, 17, parseInt(hours), parseInt(minutes), parseInt(seconds));
			}
			return newDate;
		},
		validatorInitHour(value, args) {
			return this.stringTimeToDate(value) > this.stringTimeToDate(new Date());
		},
		validatori(value, args) {
			return this.stringTimeToDate(value) > this.stringTimeToDate(this.init_hour);
		},
		fetchDataCategory(category) {
			const payload = {
				role: [],
				category,
				typology: [],
			};
			this.$store
				.dispatch("moduleResource/fetchDataSelect", payload)
				.then((res) => {
					this.categories = res.data.category;
				})
				.catch((error) => {
					console.error(error);
				});
		},
		setDataModelDinamicFields(categoryId) {
			const found = this.categories.find((typology) => typology.category_id === categoryId);

			this.dinamicFields = this.event.dataModelEventDinamicFields(found);

			// Agregar campos dinamicos al objeto danger para el renderizado
			if (this.dinamicFields.length > 0) {
				this.dinamicFields.forEach((m) => {
					this.additional_info_new[m.name] = "";
				});
			}
		},
		removeUnsavedEvents(index) {
			this.$store.commit("moduleSchedule/REMOVE_UNSAVED_EVENT", index);
		},
		async changeStatusEvent() {
			const event = {
				event_id: this.dataEvent.id,
				resource_id: this.resourceSelected.id,
				event_status_id: 7,
			};
			try {
				this.openLoading();

				const response = await this.$store.dispatch("moduleSchedule/changeStatusEvent", event);

				if (response.status === 201) {
					this.$vs.notify({
						color: "success",
						title: this.$t("TitleConfirmEventFinish"),
						text: this.$t("TextConfirmEventFinish"),
					});

					this.handleRedirect();
				}
			} catch (error) {
				this.showErrorAlert();
			} finally {
				this.closeLoading();
			}
		},
		searchVisitPointFilterCategory() {
			if (this.$refs.DinamicFields !== undefined && this.searchField.value === "AdditionalFields") {
				// recuperamos todas las propiedades adicionales modificadas desde el componente hijo
				const additional_info = this.$refs.DinamicFields.getAddionalInfo();
				// Formato para buscar por campos adicionales [['property_name', 'property_value']]
				const formatData = Object.entries(additional_info);
				this.$refs.VisitPointList.searchByAdditionalFields(
					[this.additionalField.typology_id],
					formatData,
					1,
					this.exact_values
				);
				return;
			}

			if (this.searchField.value === "notSchedule") {
				this.$refs.VisitPointList.visitPointNotSchedule(this.startDateFilter, this.endDateFilter);
				return;
			}

			this.$refs.VisitPointList.searchResource2(this.searchField.value, this.searchQuery.trim(), 1);
		},
		refres() {
			this.$refs.VisitPointList.refreshVisitPoint();
			this.searchQuery = "";
			this.searchField = {value: "Name", label: this.$t("Name")};
		},

		async fetchResourceById(userId) {
			const payload = {
				ids: [+userId],
				fields: ["id", "name", "lastname", "username", "identification", "email", "activity_id"],
			};

			this.openLoading();

			const response = await this.$store.dispatch("moduleResource/getResourcesByFilter", payload);

			this.closeLoading();

			if (response.hasOwnProperty("error") && response.error.length > 0) {
				this.validateErrorMessages(response.error[0].message);
				return;
			}

			if (response.data && response.data.records.length > 0) {
				this.resourceSelected = response.data.records[0];
				this.rcSelect = this.resourceSelected.name + " " + this.resourceSelected.lastname;
			}
		},

		fetchVisitPointById(visitPointId) {
			this.openLoading();
			this.$store
				.dispatch("moduleVisitPoint/fetchVisitPointById", visitPointId)
				.then((res) => {
					if (res) {
						this.visitPointSelected = res;
						this.vpSelect = this.visitPointSelected.name;
						this.fillEventForm(res);
					}
				})
				.catch((err) => {
					if (err.response.status === 404) {
						console.error(err);
						return;
					}
					console.error(err);
				})
				.finally(() => {
					this.closeLoading();
				});
		},

		handleVPSelect(data) {
			this.visitPointSelected = data;
			this.vpSelect = this.visitPointSelected.name;
		},

		handleResourceSelected(data) {
			this.resourceSelected = data;
			this.rcSelect = this.resourceSelected.name + " " + this.resourceSelected.lastname;
		},

		firstDayOfCurrentMonth(currentDate = new Date()) {
			const date = currentDate;
			return new Date(date.getFullYear(), 0, 1);
		},

		lastDayOfCurrentMonth(currentDate = new Date()) {
			const date = currentDate;
			return new Date(date.getFullYear(), 11, 31);
		},

		async createScheduleWaypoint(eventId) {
			const payload = {
				event_id: eventId,
				waypoints: this.waypoints.map((waypoint) => waypoint.getDataToCreate()),
			};

			this.openLoading();

			const response = await this.$store.dispatch("moduleSchedule/createScheduleWaypoint", payload);

			this.closeLoading();

			if (response.status === 201) {
				this.$vs.notify({
					color: "success",
					title: this.$t("TitleConfirmEventCreate"),
					text: this.$t("TextConfirmEventCreate"),
				});

				// clear the waypoints from the store
				this.$store.commit("moduleSchedule/CLEAN_WAYPOINTS");

				this.handleRedirect();

				return;
			}

			// validate if the response is an error
			if (response.hasOwnProperty("error") && response.error.length > 0) {
				this.validateErrorMessages(response.error[0].message);
			}
		},

		async fetchScheduleWaypoints(eventId) {
			const payload = {
				event_id: [eventId].map((id) => +id),
				order_by: ["order"],
				sort: "asc",
			};

			const response = await this.$store.dispatch("moduleSchedule/fetchScheduleWaypoints", payload);

			if (response && response.data.records.length > 0) {
				this.waypoints = response.data.records.map((waypoint) => new Waypoint(waypoint));
			}
		},

		/**
		 * Fetches event details by event ID.
		 *
		 * This method constructs a payload with the event ID, date range, and required fields,
		 * then dispatches an action to fetch the event details from the store. If the response
		 * contains event data, it sets the event data to `dataEvent` and fetches the related
		 * visit point details.
		 *
		 * @param {number} eventId - The ID of the event to fetch.
		 * @returns {Promise<void>} - A promise that resolves when the event data is fetched.
		 */
		async fetchEventById(eventId, eventDay) {
			const payload = {
				id: [+eventId],
				init_date: DateUtils.toDefaultDateTime(eventDay, "YYYY-MM-DD"),
				end_date: DateUtils.toDefaultDateTime(eventDay, "YYYY-MM-DD"),
				fields: ["on_demand", "event_init_hour", "event_end_hour", "additional_info"],
			};

			this.openLoading();

			try {
				const response = await this.$store.dispatch("moduleSchedule/fetchEventById", payload);

				if (response && response.length > 0) {
					this.dataEvent = new Event(response[0]);
					this.event = this.dataEvent;
					this.relatedIds = [];
					this.relatedIds.push(this.dataEvent.visit_point_id);
					this.fetchVisitPointById(this.dataEvent.visit_point_id);
				}
			} catch (error) {
				console.error(error);
				// go back to the previous page
				this.$router.go(-1);
			} finally {
				this.closeLoading();
			}
		},

		/**
		 * Fetches the list of activities from the store and updates the activity list.
		 *
		 * This method is important to ensure that the activity list is up-to-date when the resource is selected.
		 * also when the user is editing an event because the activity list may have changed immediately before the event is updated.
		 *
		 * @returns {Promise<void>} A promise that resolves when the fetch operation is complete.
		 */
		async fetchActivity() {
			this.openLoading();

			try {
				const response = await this.$store.dispatch("moduleActivity/fetchActivityListItems");
				if (response && response.data && response.data.length > 0) {
					this.activityList = this.$store.state.moduleActivity.activities;
				}
			} catch (error) {
				console.error(error);
			} finally {
				this.closeLoading();
			}
		},

		/**
		 * Generate a new event object with the data from the form
		 * @returns {Event}
		 *
		 */
		generateNewEvent() {
			const dateI = new Date(this.startDate);
			const dateE = new Date(this.endDate);

			const newEvent = new Event({
				activity_id: this.selectedActivity.value,
				init_date: DateUtils.toDefaultDateTime(dateI, "YYYY-MM-DD"),
				end_date: DateUtils.toDefaultDateTime(dateE, "YYYY-MM-DD"),
				init_hour: this.init_hour,
				end_hour: this.end_hour,
				repeat: this.repeatFormatString,
				resource_id: this.resourceSelected ? this.resourceSelected.id : null,
				visit_point_id: this.currentVisitPointId,
			});

			// recover all the additional properties modified from the child component
			if (this.selectedCategory) {
				if (this.$refs.DinamicFields !== undefined) {
					const additional_info = this.$refs.DinamicFields.getAddionalInfo();
					Object.assign(newEvent.additional_info, additional_info);
				}
			}

			// if is a event of type ON_DEMAND, remove the resource_id property from the payload
			if (this.moduleName === "ON_DEMAND") {
				const {resource_id, ...rest} = newEvent;
				return rest;
			}

			return newEvent;
		},

		/**
		 * Generates a new event object to update based on the current data event.
		 *
		 * @returns {Event} The new event object with updated information.
		 *
		 * The function performs the following steps:
		 * 1. Creates a new Event object using the current data event.
		 * 2. Sets the `date_prog` property of the new event to the formatted start date.
		 * 3. Copies the `additional_info` from the current data event to the new event.
		 * 4. If a valid visit point ID is selected, sets the visit point information on the new event.
		 * 5. If a category is selected, removes any additional info properties from the new event that are not present in `additional_info_new`.
		 */
		generateNewEventToUpdate() {
			const newEvent = new Event({...this.dataEvent});

			newEvent["date_prog"] = DateUtils.toDefaultDateTime(this.startDate, "YYYY-MM-DD");
			newEvent.additional_info = this.additional_info;

			if (newEvent.resource_id !== this.resourceSelected.id) {
				newEvent.resource_id = this.resourceSelected.id;
			}

			if (this.currentVisitPointId > 0) {
				newEvent.setVisitPointInfo(this.visitPointSelected);
			}

			if (this.selectedCategory) {
				for (const key in newEvent.additional_info) {
					if (!this.additional_info_new.hasOwnProperty(key)) {
						delete newEvent.additional_info[key];
					}
				}
			}

			return newEvent;
		},

		/**
		 * Adds more events to the schedule.
		 *
		 * This method performs the following steps:
		 * 1. Checks if the current visit point ID is valid. If not, sets an error flag and exits.
		 * 2. Validates all form fields using the validator.
		 * 3. If validation is successful:
		 *    - Generates a new event object.
		 *    - Sets the activity name and visit point name for the new event.
		 *    - Commits the new event to the Vuex store as an unsaved event.
		 *    - Clears the selected row in the visit point list and resets the visit point selection.
		 *    - Increments the initial time to schedule more events based on the service time of the current activity.
		 * 4. If validation fails, displays a notification with an error message.
		 */
		addMoreEvents() {
			this.$validator.validateAll().then((result) => {
				if (result) {
					const newEvent = this.generateNewEvent();

					newEvent.activity_name = this.selectedActivity.label;
					newEvent.visit_point_name = this.visitPointSelected.name;

					this.$store.commit("moduleSchedule/ADD_UNSAVED_EVENT", {
						...newEvent,
					});

					this.$refs.VisitPointList.clearSelectedRow();

					// Increment the initial time to schedule more events
					const currentActivity = this.$store.getters["moduleActivity/getActivityById"](
						this.selectedActivityLocal.value
					);
					const activityTime = currentActivity.service_time;
					const [hours, minutes, seconds] = activityTime.split(":");

					this.init_hour = DateUtils.addHoursMinutesSecodons(
						this.init_hour,
						parseInt(hours),
						parseInt(minutes),
						parseInt(seconds)
					);
				} else {
					this.$vs.notify({
						color: "danger",
						title: this.$t("Schedule.ValidData.Title"),
						text: this.$t("Schedule.ValidData.Text"),
					});
				}
			});
		},

		showPopup(val) {
			this.resourcePopupActive = val;
		},

		/**
		 * Handles changes to the waypoints configurations.
		 *
		 * @param {Array} data - The new waypoints data. Defaults to an empty array.
		 * @param {string} action - The action to perform. Defaults to "add".
		 */
		waypointsConfigurationsHandleChanges(data = [], action = "add") {
			this.waypoints = data;
			this.$store.commit("moduleSchedule/EDIT_WAYPOINT", data);
		},

		/**
		 * Fills the event form with data from the `dataEvent` object.
		 *
		 * It also finds the activity associated with the event by matching `data.activity_id` with the `value` of an activity in the `activities` array.
		 * If a matching activity is found, it sets `selectedActivity` to the found activity and updates `data.activity_name` and `data.visit_point_name`.
		 *
		 * Finally, it commits the updated `data` object to the Vuex store under the `moduleSchedule/ADD_UNSAVED_EVENT` mutation.
		 */
		fillEventForm() {
			const data = this.dataEvent;
			// DatePiker always takes one day less, so we add one day to the start date before setting it
			this.startDate = DateUtils.addDaysToDate(data.date_prog, 1);
			this.endDate = DateUtils.addDaysToDate(data.date_prog, 1);
			this.init_hour = data.init_hour;
			this.end_hour = data.end_hour;
			this.address = data.address;

			this.additional_info = {...data.additional_info};

			const foundActivity = this.activities.find((act) => act.value === data.activity_id);

			if (foundActivity) {
				this.selectedActivity = {...foundActivity};

				data.activity_name = this.selectedActivity.label;
				data.visit_point_name = this.vpSelect;

				this.$store.commit("moduleSchedule/ADD_UNSAVED_EVENT", {
					...data,
				});
			}
		},

		/**
		 * Handles actions for the assigned event list.
		 *
		 * @param {string} action - The action to be performed. Possible values are:
		 *   - "edit": Toggles the event information form.
		 *   - "cancel": Opens a confirmation dialog for canceling the event.
		 *   - "finalize": Opens a confirmation dialog for finalizing the event.
		 */
		handleAssignedEventListActions(action) {
			switch (action) {
				case "edit":
					this.toggleEventInformationForm = !this.toggleEventInformationForm;
					break;
				case "cancel":
					this.openActionConfirm("cancel");
					break;
				case "finalize":
					this.openActionConfirm("finalize");
					break;
				default:
					break;
			}
		},

		/**
		 * Opens a confirmation dialog for the specified action.
		 *
		 * @param {string} action - The action to confirm, either "cancel" or "finalize".
		 * @returns {void}
		 *
		 */
		openActionConfirm(action) {
			const title = action === "cancel" ? this.$t("Crud.Cancel") : this.$t("Crud.Finalize");
			const text = this.$t("Schedule.ConfirmActionQuestion", {n: title.toLowerCase()});

			this.$vs.dialog({
				type: "confirm",
				color: "danger",
				title,
				text,
				accept: () => (action === "cancel" ? this.removeEvent() : this.changeStatusEvent()),
				acceptText: this.$t("Accept"),
				cancelText: this.$t("Cancel"),
			});
		},

		handleBtnCancel() {
			// go back to the previous page if the on-demand module is selected
			if (this.moduleName === "calendar") {
				this.$router.push(`/apps/calendar/resource-calendar/${this.resourceSelected.id}`);
				return;
			}

			// go back previous page
			this.$router.go(-1);
		},

		/**
		 * Scrolls the window to the bottom of the page.
		 * Uses smooth scrolling behavior for a gradual scroll effect.
		 */
		scrollToBottom() {
			window.scrollTo({
				top: document.body.scrollHeight,
				behavior: "smooth", // Optional, adds animation to scroll smoothly or slow
			});
		},

		/**
		 * Redirects the user to the previous page.
		 */
		handleRedirect() {
			// depending on the module, redirect to the corresponding page
			if (this.moduleName === "ON_DEMAND") {
				this.$router.push("/apps/on-demand");
			} else {
				// redirect to the calendar page
				this.$router.push(`/apps/calendar/resource-calendar/${this.resourceSelected.id}`);
			}
		},
	},
	watch: {
		selectedActivity(val) {
			const foundActivity = this.$store.state.moduleActivity.activities.find(
				(activity) => activity.id === val.value
			);
			this.drPointsActivity = foundActivity ? foundActivity.dr_points : 0;
		},
		repeat(repeat) {
			this.repeatFormatString = repeat.map(({value}) => value).join("|");
		},

		startDate(val) {
			if (val) {
				this.endDate = val;
			}
		},

		init_hour(val) {
			if (typeof val === "string" && val !== "" && this.selectedActivity.value > 0) {
				const currentActivity = this.$store.getters["moduleActivity/getActivityById"](
					this.selectedActivity.value
				);

				if (currentActivity) {
					const activityTime = currentActivity.service_time;
					const [hours, minutes, seconds] = activityTime.split(":");
					// startTime , hours, minutes, seconds

					this.end_hour = DateUtils.addHoursMinutesSecodons(
						val,
						parseInt(hours),
						parseInt(minutes),
						parseInt(seconds)
					);
					this.init_hour_error = false;
				}
			}
		},

		selectedCategory(val) {
			// Setear modelo de datos para renderizar campos dinamicos
			this.event.additional_info = {};
			this.additional_info_new = {};
			if (val) this.setDataModelDinamicFields(val.value);
		},
		additionalField(obj) {
			this.additional_info = {};
			// this.payloadResource.typology_id = obj.value;
			let dataFields = obj && obj.hasOwnProperty("json_properties") ? obj.json_properties : [];
			dataFields = dataFields.filter((element) => element.name !== "additional_info");
			const updateData = dataFields.map((element) => {
				return {...element, error: false};
			});
			this.model = [...updateData.filter(({additional}) => additional)];
		},
		unsavedEvents(val) {
			if (val.length > 1) {
				this.checkBoxAddWaypoint = false;
			}
		},
		checkBoxAddWaypoint(val) {
			if (val) {
				setTimeout(() => {
					this.scrollToBottom();
				}, 100);
			}
		},
	},
	created() {
		const resourceId = this.$route.params.resource_id;

		Date.prototype.addMinutes = function (min) {
			this.setTime(this.getTime() + min * 60 * 1000);
			return this;
		};

		Validator.extend("truthy", this.validatori);
		Validator.extend("validatorInitHour", this.validatorInitHour);

		if (!moduleActivity.isRegistered) {
			this.$store.registerModule("moduleActivity", moduleActivity);
			moduleActivity.isRegistered = true;
		}

		if (!moduleSchedule.isRegistered) {
			this.$store.registerModule("moduleSchedule", moduleSchedule);
			moduleSchedule.isRegistered = true;
		}

		if (!moduleVisitPoint.isRegistered) {
			this.$store.registerModule("moduleVisitPoint", moduleVisitPoint);
			moduleVisitPoint.isRegistered = true;
		}

		if (!moduleResource.isRegistered) {
			this.$store.registerModule("moduleResource", moduleResource);
			moduleResource.isRegistered = true;
		}

		if (!moduleCommon.isRegistered) {
			this.$store.registerModule("moduleCommon", moduleCommon);
			moduleCommon.isRegistered = true;
		}

		if (this.$store.state.moduleActivity.activities.length <= 0) {
			this.fetchActivity();
		} else {
			this.activityList = this.$store.state.moduleActivity.activities;
		}

		this.fetchDataCategory("SCHEDULE");

		if (this.moduleName === "ON_DEMAND") {
			this.visitPointListConfig.tableOptions = false;
		}

		if (resourceId) this.fetchResourceById(resourceId);
	},
	mounted() {
		const eventId = this.$route.params.event_id;
		const eventDay = this.$route.params.event_day;

		if (eventDay) {
			this.startDate = eventDay;
			this.endDate = eventDay;
		}

		if (eventId) {
			this.isEdit = true;
			this.toggleEventInformationForm = false;

			this.fetchEventById(eventId, eventDay);
			this.fetchScheduleWaypoints(eventId);
		}
	},
	beforeDestroy() {
		// Clear the unsaved events
		this.$store.commit("moduleSchedule/SET_UNSAVED_EVENT", []);
	},
};
</script>

<style lang="scss" scoped>
.vs-popup--content-custom {
	.vs-popup--content {
		height: 100%;
	}

	.btn-save {
		position: absolute;
		left: cal(100 vw- 10%);
		bottom: 30px;
	}

	.btn-del {
		position: absolute;
		left: 200px;
		bottom: 30px;
	}
}
</style>
